'use client';

import { useState } from 'react';

import Link from 'next/link';

import { Analytics } from '@vercel/analytics/react';
import { MotionConfig } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LandingPageProps } from '../_lib/landing-page.types';

import { ContactSection } from '~/(marketing)/_components/contact-section';
import { SchedulingModal } from '~/(marketing)/_components/google-calendar-button';
import { BuiltWithYouSection } from '~/(marketing)/_components/home/built-with-you-section';
import { FinalCtaSection } from '~/(marketing)/_components/home/final-cta-section';
import { HeroSection } from '~/(marketing)/_components/home/hero-section';
import { LogoMarquee } from '~/(marketing)/_components/home/logo-marquee';
import { MarketReadinessSection } from '~/(marketing)/_components/home/market-readiness-section';
import { PlatformShowcase } from '~/(marketing)/_components/home/platform-showcase';
import { SolutionsTabs } from '~/(marketing)/_components/home/solutions-tabs';
import { TestimonialsSection } from '~/(marketing)/_components/home/testimonials-section';
import { MarketingFooter } from '~/(marketing)/_components/marketing-footer';
import { MarketingHeader } from '~/(marketing)/_components/marketing-header';
import { FaqList } from '~/(marketing)/_components/marketing-sections';
import {
  featuredFaqEntries,
  localizeFaqEntries,
} from '~/(marketing)/_lib/faq.content';
import { FAQ_TEASER } from '~/(marketing)/_lib/home.content';
import { localizedHref } from '~/lib/marketing/routes';
import { asMarketingLocale } from '~/lib/marketing/site';
import '~/(marketing)/_components/marketing.css';

/*
 * Apex marketing landing page, served at '/' on the root domain via the
 * landing-page registry.
 *
 * Story order: what it is (hero with a live product scene) → who it is for
 * (business-type tabs, so each visitor finds their venue first) → who trusts
 * it (logo marquee) → what it does (platform tour with live demos) → how we
 * work (built with you) → proof (testimonials) → readiness, FAQ, contact and
 * a split call to action.
 *
 * Palette: ink #202020 · graphite #2b2b2b · sand #ede8e2 · bone #f7f6f4 ·
 * smoke #dcd7cf · blue #a9c8ff. Copy lives in typed {en, ar} content files
 * under (marketing)/_lib. Every animation honours prefers-reduced-motion via
 * MotionConfig plus the demo kit's static fallbacks.
 */
export default function MainSiteLandingPage(_props: LandingPageProps) {
  const { i18n } = useTranslation('landing');
  const locale = asMarketingLocale(i18n.language);
  const isRTL = locale === 'ar';
  const [scheduleDemoOpen, setScheduleDemoOpen] = useState(false);
  const openScheduling = () => setScheduleDemoOpen(true);

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-[#f7f6f4]" dir={isRTL ? 'rtl' : 'ltr'}>
        <MarketingHeader />

        <HeroSection locale={locale} onScheduleDemo={openScheduling} />
        <SolutionsTabs locale={locale} />
        <LogoMarquee locale={locale} />
        <PlatformShowcase locale={locale} />
        <BuiltWithYouSection locale={locale} />
        <TestimonialsSection locale={locale} />
        <MarketReadinessSection locale={locale} />

        <section className="bg-[#ede8e2] px-4 py-16 md:px-8 md:py-24 lg:px-[90px]">
          <div className="mx-auto max-w-[900px]">
            <div className="mb-10 text-center md:mb-14">
              <p className="mb-4 font-roboto-mono text-sm uppercase tracking-wider text-[#202020] opacity-80">
                {FAQ_TEASER.label[locale]}
              </p>
              <h2
                className="font-roboto text-[28px] font-normal leading-[1.2] text-[#202020] md:text-[34px] lg:text-[40px]"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                {FAQ_TEASER.heading[locale]}
              </h2>
            </div>
            <FaqList entries={localizeFaqEntries(featuredFaqEntries(), locale)} />
            <div className="mt-8 text-center">
              <Link
                href={localizedHref('/faq', locale)}
                className="group inline-flex items-center gap-2 font-roboto-mono text-[12px] uppercase tracking-wide text-[#202020]/70 transition-colors hover:text-[#202020]"
              >
                {FAQ_TEASER.viewAll[locale]}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>
        </section>

        <ContactSection locale={locale} />

        <FinalCtaSection locale={locale} />

        <SchedulingModal
          open={scheduleDemoOpen}
          onClose={() => setScheduleDemoOpen(false)}
        />

        <MarketingFooter />

        <Analytics />
      </div>
    </MotionConfig>
  );
}
